import React, { useState, useEffect, useMemo } from "react";
import { X, Clock, Plus, Pencil, Trash2, ArrowUpDown } from "lucide-react";
import { dataClient as base44 } from "@/services/data";
import { format, isToday, isYesterday, isThisWeek } from "date-fns";

const ACTION_CONFIG = {
  create: { icon: Plus, color: "text-green-600", bg: "bg-green-50", border: "border-green-200", label: "Created" },
  update: { icon: Pencil, color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200", label: "Updated" },
  delete: { icon: Trash2, color: "text-red-600", bg: "bg-red-50", border: "border-red-200", label: "Deleted" },
  reorder: { icon: ArrowUpDown, color: "text-gray-600", bg: "bg-gray-100", border: "border-gray-200", label: "Reordered" },
};

const FILTER_OPTIONS = [
  { key: "all", label: "All" },
  { key: "create", label: "Created" },
  { key: "update", label: "Updated" },
  { key: "delete", label: "Deleted" },
  { key: "reorder", label: "Reordered" },
];

function parseAsUTC(s) {
  if (!s) return null;
  const str = String(s);
  if (str.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(str)) return new Date(str);
  return new Date(str + "Z");
}

function formatUTC(date) {
  const h = date.getUTCHours();
  const m = date.getUTCMinutes();
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

function getDateLabel(date) {
  if (isToday(date)) return "Today";
  if (isYesterday(date)) return "Yesterday";
  if (isThisWeek(date)) return format(date, "EEEE");
  return format(date, "EEEE, MMM d, yyyy");
}

function groupByDate(logs) {
  const groups = {};
  for (const log of logs) {
    if (!log.created_date) continue;
    const date = parseAsUTC(log.created_date);
    const key = format(date, "yyyy-MM-dd");
    if (!groups[key]) groups[key] = { label: getDateLabel(date), items: [] };
    groups[key].items.push(log);
  }
  return Object.entries(groups).sort(([a], [b]) => (a < b ? 1 : -1));
}

export default function AuditLogModal({ onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const res = await base44.functions.invoke("getAuditLog", {});
        setLogs(res.data.logs || []);
      } catch (err) {
        console.error("Failed to fetch audit logs:", err);
      }
      setLoading(false);
    };
    fetchLogs();
  }, []);

  const filteredLogs = useMemo(
    () => (filter === "all" ? logs : logs.filter((l) => l.action === filter)),
    [logs, filter]
  );

  const grouped = useMemo(() => groupByDate(filteredLogs), [filteredLogs]);
  const counts = useMemo(() => {
    const c = { all: logs.length };
    for (const l of logs) c[l.action] = (c[l.action] || 0) + 1;
    return c;
  }, [logs]);

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-50 flex items-center justify-center p-3 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[97vw] max-h-[92vh] overflow-hidden flex flex-col ring-1 ring-black/5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-500" />
            <h3 className="font-bold text-gray-900">Change History</h3>
            {!loading && (
              <span className="text-xs font-medium text-gray-400 ml-1">({logs.length})</span>
            )}
          </div>
          <button onClick={onClose} title="Close" className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!loading && logs.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 px-4 py-2.5 border-b border-gray-100">
            {FILTER_OPTIONS.map((opt) => {
              const active = filter === opt.key;
              const count = counts[opt.key] || 0;
              if (count === 0 && opt.key !== "all") return null;
              return (
                <button
                  key={opt.key}
                  onClick={() => setFilter(opt.key)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                    active
                      ? "bg-gray-900 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {opt.label}
                  <span className={`text-[10px] ${active ? "text-gray-300" : "text-gray-400"}`}>{count}</span>
                </button>
              );
            })}
          </div>
        )}

        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-gray-200 border-t-blue-600 rounded-full animate-spin" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Clock className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No changes recorded yet</p>
            </div>
          ) : grouped.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <p className="text-sm">No {FILTER_OPTIONS.find((f) => f.key === filter)?.label.toLowerCase()} entries</p>
            </div>
          ) : (
            <div className="px-4 py-3">
              {grouped.map(([dateKey, group]) => (
                <div key={dateKey} className="mb-5 last:mb-0">
                  <div className="flex items-center gap-2 mb-2 sticky top-0 bg-white py-1 z-[1]">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                      {group.label}
                    </span>
                    <span className="text-[10px] text-gray-400">· {group.items.length}</span>
                    <div className="flex-1 h-px bg-gray-100" />
                  </div>
                  <div className="space-y-1">
                    {group.items.map((log) => {
                      const config = ACTION_CONFIG[log.action] || ACTION_CONFIG.update;
                      const Icon = config.icon;
                      return (
                        <div
                          key={log.id}
                          className={`flex items-start gap-3 py-2 px-2.5 rounded-lg border ${config.border} bg-white hover:bg-gray-50 transition-colors`}
                        >
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${config.bg} ${config.color}`}>
                            <Icon className="w-3.5 h-3.5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-900">{log.details || `${config.label} ${log.node_name || ""}`}</p>
                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                              {log.created_date && (
                                <span className="text-[10px] text-gray-500 font-medium">
                                  LT - {format(parseAsUTC(log.created_date), "h:mm a")}
                                </span>
                              )}
                              {log.created_date && (
                                <>
                                  <span className="text-[10px] text-gray-300">|</span>
                                  <span className="text-[10px] text-gray-400">
                                    ST - {formatUTC(parseAsUTC(log.created_date))} UTC
                                  </span>
                                </>
                              )}
                              {log.actor && (
                                <>
                                  <span className="text-[10px] text-gray-300">|</span>
                                  <span className="text-[10px] text-gray-400">{log.actor}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}